import { isMobile } from 'react-device-detect';
import { Box, Flex, Skeleton, Stack } from '@mantine/core';

export interface ListSkeletonProps {
  count?: number;
  variant?: 'card' | 'row' | 'auto';
  imageSize?: number;
  showActions?: boolean;
  textLines?: number;
}

export default function ListSkeleton({
  count = 3,
  variant = 'auto',
  imageSize,
  showActions = true,
  textLines = 4,
}: ListSkeletonProps) {
  const isCardLayout = variant === 'card' || (variant === 'auto' && isMobile);
  const imgSize = imageSize ?? (isCardLayout ? 120 : 86);

  const renderCardItem = () => (
    <Box
      mb={10}
      bd="1px solid var(--border-color)"
      bdrs="8px"
      style={{
        boxShadow: '0 0 6px 0 var(--box-shadow)',
      }}
    >
      <Flex align="center" gap="md" px="xs" pt="xs">
        <Skeleton height={imgSize} width={imgSize + 20} radius="sm" />
        <Stack gap="xs" flex={1}>
          <Skeleton height={20} width="80%" />
          <Skeleton height={16} width="60%" />
          <Skeleton height={16} width="50%" />
        </Stack>
      </Flex>
      <Stack gap="xs" px="xs">
        {Array.from({ length: Math.max(0, textLines - 3) }, (_, i) => (
          <Skeleton key={`text-${i}`} height={16} width={`${70 - i * 10}%`} />
        ))}
      </Stack>
      {showActions && (
        <Flex
          gap="xs"
          px="xs"
          pb="xs"
          pt="xs"
          align="center"
          justify="space-between"
        >
          <Flex gap="xs">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={`action-${i}`} height={24} width={24} circle />
            ))}
          </Flex>
          <Skeleton height={36} width={120} radius="sm" />
        </Flex>
      )}
    </Box>
  );

  const renderRowItem = () => (
    <Flex
      align="center"
      gap="md"
      p="xs"
      bd="1px solid var(--border-color)"
      bdrs="4px"
      style={{
        boxShadow: '0 0 6px 0 var(--box-shadow)',
      }}
    >
      <Skeleton height={imgSize} width={imgSize} radius="sm" />
      <Stack gap="xs" flex={1}>
        {Array.from({ length: textLines }, (_, i) => (
          <Skeleton
            key={`text-${i}`}
            height={i === 0 ? 24 : 16}
            width={`${60 + (i % 3) * 10}%`}
          />
        ))}
      </Stack>
      {showActions && (
        <Flex gap="xs" ml="auto" align="center">
          <Skeleton height={36} width={120} radius="sm" />
        </Flex>
      )}
    </Flex>
  );

  return (
    <Stack gap="md" w="100%">
      {Array.from({ length: count }, (_, index) => (
        <div key={`skeleton-item-${index}`}>
          {isCardLayout ? renderCardItem() : renderRowItem()}
        </div>
      ))}
    </Stack>
  );
}
